<template>
  <section v-if="video" class="cv" :class="{ 'cv-tint': tint, 'cv-article': article }" :aria-labelledby="`cv-title-${video.key}`">
    <div class="cv-wrap">
      <header class="cv-head">
        <p v-if="!plain">{{ video.eyebrow || 'Video comparison' }}</p>
        <h2 :id="`cv-title-${video.key}`">{{ video.heading || `Watch ${video.competitor} vs Workspace369, feature by feature` }}</h2>
        <p class="cv-sub">{{ video.summary }}</p>
      </header>

      <div ref="playerRef" class="cv-player">
        <iframe
          :key="`${playerSrc}:${replayCount}`"
          :src="playerSrc"
          :title="video.title"
          width="1280"
          height="720"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
      <p class="cv-meta">
        <a :href="watchUrl" target="_blank" rel="noopener">Watch on YouTube</a>
        <span aria-hidden="true">·</span>
        <span translate="no">{{ video.length }}</span>
      </p>

      <h3 class="cv-h3">Chapters</h3>
      <ol class="cv-chapters">
        <li v-for="chapter in video.chapters" :key="chapter.start">
          <a :href="`${watchUrl}&t=${chapter.start}s`" target="_blank" rel="noopener" @click="playFrom($event, chapter.start)">
            <span class="cv-time" translate="no">{{ timestamp(chapter.start) }}</span>
            <span>{{ chapter.label }}</span>
          </a>
        </li>
      </ol>

      <p v-for="(note, index) in video.corrections || []" :key="`c${index}`" class="cv-correction">{{ note }}</p>
      <details class="cv-transcript">
        <summary>Read the full transcript</summary>
        <p v-for="(paragraph, index) in video.transcript" :key="index">{{ paragraph }}</p>
      </details>
    </div>
  </section>
</template>

<script setup>
// One generated module per video (modules/page-data.ts), keyed by the page paths it belongs on.
import comparisonVideoLoaders from '#build/page-data/comparison-video-loaders.mjs';

// The embedded comparison video for a /compare or /alternatives page (nothing renders when the page has none).
// Same player as TutorialPage: a plain youtube-nocookie iframe in the server-rendered HTML so Google sees the video,
// and a chapter click restarts the embed at that chapter. Emits its own VideoObject with Clip chapters + transcript.
const props = defineProps({
  path: { type: String, required: true },
  canonicalUrl: { type: String, required: true },
  pageId: { type: String, default: '' },
  tint: { type: Boolean, default: false },
  // article = the AlternativesPage text column (46rem, ruled heading) instead of the comparison page's 64rem band
  article: { type: Boolean, default: false },
  // plain = no eyebrow line above the heading (the alternatives v2 proposal has no kickers)
  plain: { type: Boolean, default: false },
});

const siteUrl = 'https://workspace369.com';
// Same lookup as comparisonVideoForPath in data/comparison-videos.ts (trailing slash ignored).
const loadVideo = async (path) => (await comparisonVideoLoaders[path.replace(/\/$/, '')]?.()) || null;
const video = ref(await loadVideo(props.path));
watch(() => props.path, async (path) => {
  video.value = await loadVideo(path);
});
const watchUrl = computed(() => `https://www.youtube.com/watch?v=${video.value?.youtubeId}`);
const embedBase = computed(() => `https://www.youtube-nocookie.com/embed/${video.value?.youtubeId}?rel=0`);

const startAt = ref(null);
const replayCount = ref(0);
const playerRef = ref(null);
const playerSrc = computed(() =>
  startAt.value === null ? embedBase.value : `${embedBase.value}&start=${startAt.value}&autoplay=1`,
);

const playFrom = (event, seconds) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  startAt.value = seconds;
  replayCount.value++;
  playerRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const timestamp = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const durationSeconds = (iso) => {
  const match = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/.exec(iso) || [];
  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
};

const videoSchema = computed(() => {
  const v = video.value;
  const total = durationSeconds(v.duration);
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${props.canonicalUrl}#video`,
    name: v.title,
    description: v.videoDescription,
    thumbnailUrl: [
      `https://i.ytimg.com/vi/${v.youtubeId}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`,
    ],
    uploadDate: v.uploadDate,
    duration: v.duration,
    contentUrl: watchUrl.value,
    embedUrl: `https://www.youtube.com/embed/${v.youtubeId}`,
    transcript: v.transcript.join('\n\n'),
    publisher: { '@id': `${siteUrl}/#organization` },
    about: { '@id': `${siteUrl}/#software` },
    ...(props.pageId ? { isPartOf: { '@id': props.pageId } } : {}),
    hasPart: v.chapters.map((chapter, index) => ({
      '@type': 'Clip',
      name: chapter.label,
      startOffset: chapter.start,
      endOffset: v.chapters[index + 1]?.start ?? total,
      url: `${watchUrl.value}&t=${chapter.start}s`,
    })),
  };
});

useHead(() => (video.value ? {
  script: [
    {
      key: `comparison-video-schema-${video.value.key}`,
      type: 'application/ld+json',
      children: JSON.stringify(videoSchema.value),
    },
  ],
} : {}));
</script>

<style scoped>
.cv { padding: 3.25rem 0; }
@media (min-width: 768px) { .cv { padding: 4.25rem 0; } }
.cv-tint { background: #fafafa; border-block: 1px solid #f0f1f3; }
.dark .cv-tint { background: #0b1222; border-color: #1e293b; }

.cv-wrap {
  max-width: 64rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}
@media (min-width: 640px) { .cv-wrap { padding-inline: 1.75rem; } }

.cv-article { padding: 0; margin-top: 4rem; }
.cv-article .cv-wrap { width: min(100% - 2rem, 46rem); max-width: none; padding-inline: 0; }
.cv-article .cv-head { max-width: none; }
.cv-article .cv-head h2 { border-top: 1px solid #e2e8f0; padding-top: 2.5rem; font-size: clamp(1.45rem, 2.6vw, 1.8rem); line-height: 1.18; }
.dark .cv-article .cv-head h2 { border-top-color: #2c2c2c; }

.cv-head { max-width: 44rem; margin-bottom: 1.6rem; }
.cv-head > p:first-child {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.dark :is(.cv-tint, .cv-article) .cv-head > p:first-child { color: #67e8f9; }
.cv-head h2 {
  margin-top: 0.55rem;
  color: #020617;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
  overflow-wrap: break-word;
}
.dark :is(.cv-tint, .cv-article) .cv-head h2 { color: #f8fafc; }
.cv-sub {
  margin-top: 0.8rem;
  color: #3f4c63;
  font-size: 1.02rem;
  line-height: 1.7;
}
.dark :is(.cv-tint, .cv-article) .cv-sub { color: #cbd5e1; }

.cv-player {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  width: 100%;
  border: 1px solid #dbe4ee;
  border-radius: 0.9rem;
  background: #020617;
  box-shadow: 0 24px 60px rgb(15 23 42 / 0.12);
}
.dark :is(.cv-tint, .cv-article) .cv-player { border-color: #243247; }
.cv-player iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.cv-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.85rem;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.cv-meta a { color: #0e7490; text-decoration: underline; text-underline-offset: 0.2em; }
.dark :is(.cv-tint, .cv-article) .cv-meta { color: #94a3b8; }
.dark :is(.cv-tint, .cv-article) .cv-meta a { color: #67e8f9; }

.cv-h3 {
  margin-top: 2rem;
  color: #020617;
  font-size: 1.1rem;
  font-weight: 800;
}
.dark :is(.cv-tint, .cv-article) .cv-h3 { color: #f8fafc; }

.cv-chapters {
  display: grid;
  gap: 0.35rem 1.5rem;
  margin-top: 0.8rem;
  padding: 0;
  list-style: none;
}
@media (min-width: 640px) { .cv-chapters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.cv-chapters a {
  display: flex;
  gap: 0.75rem;
  align-items: baseline;
  padding: 0.45rem 0;
  border-bottom: 1px solid #eef2f6;
  color: #0f172a;
  font-size: 0.97rem;
  font-weight: 600;
}
.dark :is(.cv-tint, .cv-article) .cv-chapters a { border-color: #1e293b; color: #e2e8f0; }
.cv-chapters a:hover { color: #0e7490; }
.cv-time {
  min-width: 2.6rem;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.dark :is(.cv-tint, .cv-article) .cv-time { color: #67e8f9; }

.cv-correction {
  overflow-wrap: anywhere;
  margin: 1rem 0 0;
  border-left: 3px solid #b45309;
  padding: 0.5rem 0.75rem;
  background: #fffbeb;
  color: #7c2d12;
  font-size: 0.95rem;
  line-height: 1.6;
}
.dark :is(.cv-tint, .cv-article) .cv-correction { background: #2a1d0a; color: #fcd9a8; border-left-color: #f59e0b; }
.cv-transcript {
  margin-top: 1.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.9rem 1.1rem;
  background: #fff;
}
.dark :is(.cv-tint, .cv-article) .cv-transcript { border-color: #243247; background: #0f172a; }
.cv-transcript summary {
  cursor: pointer;
  color: #0f172a;
  font-weight: 800;
}
.dark :is(.cv-tint, .cv-article) .cv-transcript summary { color: #f8fafc; }
.cv-transcript p {
  margin: 0.9rem 0 0;
  color: #3f4c63;
  font-size: 0.98rem;
  line-height: 1.75;
}
.dark :is(.cv-tint, .cv-article) .cv-transcript p { color: #cbd5e1; }
</style>
